// src/Components/GoogleMap/PolygonDraw.js

import { useState, useRef, useCallback, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';

export function usePolygonDraw(
  mapInstance,
  setPolygonsData,
  setSelectedMarkerData,
  onDebugUpdate,
  setActivePolygonId,
  enablePolyEditing,
  drawingStyles, // AMENDED: Receive drawingStyles to apply custom colors to the draft
) {
  const [isDrawing, setIsDrawing] = useState(false);
  const isDrawingRef = useRef(false);
  const isNewPolygonRef = useRef(false);
  const currentPolyRef = useRef(null);
  const pathRef = useRef([]);
  const tempVerticesRef = useRef([]);

  // DOM Listener Ref for the Nuclear Fix
  const domDblClickHandlerRef = useRef(null);

  const tempIdRef = useRef(null);
  const setSelectedMarkerDataRef = useRef(setSelectedMarkerData);

  // AMENDED: Keep a perfectly fresh ref of the styles to avoid stale closures during click events
  const drawingStylesRef = useRef(drawingStyles);

  useEffect(() => {
    drawingStylesRef.current = drawingStyles;
  }, [drawingStyles]);

  useEffect(() => {
    isDrawingRef.current = isDrawing;
  }, [isDrawing]);

  useEffect(() => {
    setSelectedMarkerDataRef.current = setSelectedMarkerData;
  }, [setSelectedMarkerData]);

  // --- HELPER: Create Advanced Marker Handle ---
  const createVertexHandle = useCallback((color) => {
    const div = document.createElement('div');
    // AMENDED: INCREASED SIZE 100% (4px -> 8px)
    div.style.width = '8px';
    div.style.height = '8px';
    div.style.backgroundColor = color;
    // THINNER BORDER: 1.5px -> 1px
    div.style.border = '1px solid white';
    div.style.borderRadius = '50%';
    div.style.boxShadow = '0 1px 2px rgba(0,0,0,0.3)';
    // Important: pointer-events: none mimics clickable: false
    div.style.pointerEvents = 'none';
    return div;
  }, []);

  // AMENDED: Live updating effect so the drawing line changes color instantly mid-draw
  useEffect(() => {
    if (isDrawingRef.current && currentPolyRef.current) {
      const activeColor = drawingStyles?.polygonColor || '#FF9900';
      currentPolyRef.current.setOptions({ strokeColor: activeColor });

      tempVerticesRef.current.forEach((v) => {
        if (v) v.content = createVertexHandle(activeColor);
      });
    }
  }, [drawingStyles?.polygonColor, createVertexHandle]);

  // --- CANCEL/STOP ---
  const cancelDrawing = useCallback(
    (reason = 'Cancelled') => {
      if (currentPolyRef.current) {
        currentPolyRef.current.setMap(null);
        currentPolyRef.current = null;
      }

      // Cleanup Vertices (AdvancedMarkers)
      tempVerticesRef.current.forEach((v) => {
        if (v) v.map = null;
      });
      tempVerticesRef.current = [];

      // --- NUCLEAR FIX: Remove DOM Listener ---
      if (mapInstance.current && domDblClickHandlerRef.current) {
        const mapDiv = mapInstance.current.getDiv();
        mapDiv.removeEventListener('dblclick', domDblClickHandlerRef.current, true);
        domDblClickHandlerRef.current = null;
      }

      setIsDrawing(false);
      isNewPolygonRef.current = false;
      pathRef.current = [];
      tempIdRef.current = null;

      if (mapInstance.current) {
        mapInstance.current.setOptions({ draggableCursor: 'grab' });

        // Safe zoom re-enable
        setTimeout(() => {
          if (mapInstance.current) {
            mapInstance.current.setOptions({ disableDoubleClickZoom: false });
          }
        }, 800);
      }

      // if (onDebugUpdate) {
      //   onDebugUpdate({ stage: reason.toUpperCase(), points: 0 });
      // }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mapInstance, onDebugUpdate],
  );

  // --- COMPLETE ---
  const completePolygon = useCallback(
    (google, skipStateUpdate = false) => {
      if (!isDrawingRef.current || !currentPolyRef.current) return null;

      const path = pathRef.current;
      if (path.length < 3) {
        cancelDrawing('Too few points');
        return null;
      }

      const newId = tempIdRef.current || uuidv4();

      // AMENDED: Ensure the completed polygon data matches the currently selected drawing styles
      const finalColor = drawingStylesRef.current?.polygonColor || '#FF9900';

      const newPolyData = {
        id: newId,
        type: 'polygon',
        paths: path,
        color: finalColor,
        isNew: true,
        lastModified: new Date().toISOString(),
      };

      if (!skipStateUpdate) {
        setPolygonsData((prev) => [...prev, newPolyData]);
      }

      if (currentPolyRef.current) {
        currentPolyRef.current.setMap(null);
        currentPolyRef.current = null;
      }

      // Cleanup Vertices
      tempVerticesRef.current.forEach((v) => {
        if (v) v.map = null;
      });
      tempVerticesRef.current = [];

      // Nuclear cleanup of listener
      if (mapInstance.current && domDblClickHandlerRef.current) {
        const mapDiv = mapInstance.current.getDiv();
        mapDiv.removeEventListener('dblclick', domDblClickHandlerRef.current, true);
        domDblClickHandlerRef.current = null;
      }

      setIsDrawing(false);
      isNewPolygonRef.current = false;
      pathRef.current = [];
      tempIdRef.current = null;

      if (mapInstance.current) {
        mapInstance.current.setOptions({ draggableCursor: 'grab' });

        // Safe zoom re-enable
        setTimeout(() => {
          if (mapInstance.current) {
            mapInstance.current.setOptions({ disableDoubleClickZoom: false });
          }
        }, 800);
      }

      if (!skipStateUpdate) {
        setActivePolygonId(newId);

        if (setSelectedMarkerDataRef.current) {
          setSelectedMarkerDataRef.current(newPolyData);
        }

        setTimeout(() => {
          if (enablePolyEditing) enablePolyEditing(newId);
        }, 100);
      }

      return newPolyData;
    },
    [mapInstance, cancelDrawing, setPolygonsData, setActivePolygonId, enablePolyEditing],
  );

  // --- START ---
  const startDrawing = useCallback(
    (incomingGoogle) => {
      const google = incomingGoogle?.maps ? incomingGoogle : window.google;
      if (!mapInstance.current || !google) return;

      setIsDrawing(true);
      isNewPolygonRef.current = true;
      pathRef.current = [];

      tempVerticesRef.current.forEach((v) => {
        if (v) v.map = null;
      });
      tempVerticesRef.current = [];

      tempIdRef.current = uuidv4();

      // AMENDED: Draw the temporary line using the current active style directly from ref
      const activeColor = drawingStylesRef.current?.polygonColor || '#FF9900';

      const tempPolyLine = new google.maps.Polyline({
        strokeColor: activeColor,
        strokeOpacity: 0.8,
        strokeWeight: 2,
        map: mapInstance.current,
        clickable: false,
        zIndex: 30,
      });
      tempPolyLine.setPath([]);
      currentPolyRef.current = tempPolyLine;

      if (mapInstance.current) {
        mapInstance.current.setOptions({ draggableCursor: 'crosshair' });

        // Disable API Zoom
        mapInstance.current.setOptions({ disableDoubleClickZoom: true });

        // --- NUCLEAR FIX: Add DOM Capture Listener ---
        const mapDiv = mapInstance.current.getDiv();
        const handleDomDblClick = (e) => {
          e.stopPropagation();
          e.preventDefault();

          // --- FIX: Now we actually COMPLETE the polygon on double click ---
          completePolygon(google);
        };

        mapDiv.addEventListener('dblclick', handleDomDblClick, true);
        domDblClickHandlerRef.current = handleDomDblClick;
      }

      if (setSelectedMarkerDataRef.current) {
        setSelectedMarkerDataRef.current({
          id: tempIdRef.current,
          type: 'polygon',
          paths: [],
          color: activeColor,
          isDrawing: true,
          isNew: true,
        });
      }

      // if (onDebugUpdate) {
      //   onDebugUpdate({
      //     stage: 'START DRAWING',
      //     points: 0,
      //     cursor: 'Waiting...',
      //     lastPoint: 'None',
      //     rubberBand: 'Active',
      //   });
      // }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mapInstance, onDebugUpdate, completePolygon],
  );

  // --- CLICK ---
  const handleDrawClick = useCallback(
    (latLng) => {
      if (!isDrawingRef.current || !currentPolyRef.current || !latLng) return;

      const google = window.google;
      const lat = typeof latLng.lat === 'function' ? latLng.lat() : latLng.lat;
      const lng = typeof latLng.lng === 'function' ? latLng.lng() : latLng.lng;

      pathRef.current.push({ lat, lng });

      const path = currentPolyRef.current.getPath();
      while (path.getLength() < pathRef.current.length) path.push(latLng);
      path.setAt(pathRef.current.length - 1, latLng);

      // AMENDED: Ensure the point handles match the active color dynamically from ref
      const activeColor = drawingStylesRef.current?.polygonColor || '#FF9900';

      // Drop Vertex Marker (UPDATED to AdvancedMarkerElement with smaller handle)
      if (google && google.maps && google.maps.marker) {
        const vertex = new google.maps.marker.AdvancedMarkerElement({
          position: latLng,
          map: mapInstance.current,
          content: createVertexHandle(activeColor), // Use helper for visual dot
          zIndex: 40,
        });
        tempVerticesRef.current.push(vertex);
      }

      // if (onDebugUpdate) {
      //   onDebugUpdate({
      //     stage: 'POINT ADDED',
      //     points: pathRef.current.length,
      //     cursor: `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
      //     lastPoint: 'Fixed',
      //     rubberBand: 'Active',
      //   });
      // }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mapInstance, onDebugUpdate, createVertexHandle],
  );

  // --- MOVE ---
  const handleMouseMove = useCallback((latLng) => {
    // Disabled for stability
    return;
  }, []);

  return {
    isDrawing,
    isDrawingRef,
    isNewPolygonRef,
    startDrawing,
    handleDrawClick,
    handleMouseMove,
    cancelDrawing,
    completePolygon,
  };
}

/*
// ==============================================================================
// AMENDED: DEBUG HUD COMPONENT
// Moved from MapOverlays.js. Uncomment to restore heads-up-display during drawing
// ==============================================================================
// import Paper from '@mui/material/Paper';
//
// export const PolygonDebugHUD = ({ showDebug }) => {
//   if (!showDebug) return null;
//   return (
//     <Paper
//       id='debug-hud'
//       sx={{
//         position: 'absolute',
//         top: 10,
//         left: 10,
//         padding: 1.5,
//         bgcolor: 'rgba(0, 0, 0, 0.8)',
//         color: '#81c784',
//         fontFamily: 'monospace',
//         fontSize: 12,
//         zIndex: 10000,
//         pointerEvents: 'none',
//         minWidth: '200px',
//       }}>
//       Loading Debug Info...
//     </Paper>
//   );
// };
*/
